/**
 * TTS Factory Service
 * 
 * Creates the appropriate TTS service based on user preferences.
 * 
 * Provider selection:
 * - 'gemini_flash_tts': Gemini 3.1 Flash TTS (preview)
 * - 'deepgram': Uses Deepgram Aura TTS
 * - 'openai': Uses OpenAI TTS (current approach)
 */

import { deepgramTTSService, AURA_VOICES } from './deepgramTTSService';
import { geminiFlashTTSService } from './geminiFlashTTSService';
import { GEMINI_LIVE_PREBUILT_VOICES } from './geminiLiveVoices';
import {
  getVoicePreferences,
  isDeepgramTTSEnabled,
} from '../preferences/voicePreferencesService';

// TTS Provider types
export const TTS_PROVIDER = {
  OPENAI: 'openai',
  DEEPGRAM: 'deepgram',
  GEMINI_FLASH_TTS: 'gemini_flash_tts',
};

// OpenAI voices (for reference)
export const OPENAI_VOICES = {
  alloy: { gender: 'neutral', description: 'Balanced voice' },
  echo: { gender: 'male', description: 'Warm male voice' },
  fable: { gender: 'male', description: 'British male voice' },
  onyx: { gender: 'male', description: 'Deep male voice' },
  nova: { gender: 'female', description: 'Friendly female voice' },
  shimmer: { gender: 'female', description: 'Clear female voice' },
};

/**
 * Determine the TTS provider based on preferences
 * @param {Object} preferences - User's voice preferences
 * @returns {string} Provider type from TTS_PROVIDER
 */
export const determineTTSProvider = (preferences) => {
  if (preferences?.ttsProvider === 'gemini_flash_tts') {
    return TTS_PROVIDER.GEMINI_FLASH_TTS;
  }
  if (isDeepgramTTSEnabled(preferences)) {
    return TTS_PROVIDER.DEEPGRAM;
  }
  return TTS_PROVIDER.OPENAI;
};

/**
 * Get the appropriate TTS service instance
 * @param {string} userId - User ID to fetch preferences
 * @returns {Promise<Object>} { service, provider, voice }
 */
export const getTTSService = async (userId) => {
  // Get user preferences
  const preferences = await getVoicePreferences(userId);
  
  // Determine provider
  const provider = determineTTSProvider(preferences);

  if (provider === TTS_PROVIDER.GEMINI_FLASH_TTS) {
    const voiceName = preferences?.geminiLive?.voice || 'Aoede';
    geminiFlashTTSService.configure({
      apiKey: process.env.REACT_APP_GOOGLE_AI_API_KEY,
      voiceName,
    });

    return {
      service: geminiFlashTTSService,
      provider: 'gemini_flash_tts',
      voice: voiceName,
      availableVoices: GEMINI_LIVE_PREBUILT_VOICES,
    };
  }

  if (provider === TTS_PROVIDER.DEEPGRAM) {
    // Configure Deepgram TTS with user's voice preference
    const voice = preferences.deepgramSettings?.ttsVoice || 'asteria';
    deepgramTTSService.configure({ voice });
    
    return {
      service: deepgramTTSService,
      provider,
      voice,
      availableVoices: AURA_VOICES,
    };
  }

  // OpenAI TTS (return null service - caller should use existing ttsService)
  return {
    service: null, // Use existing OpenAI TTS service
    provider: TTS_PROVIDER.OPENAI,
    voice: preferences.openaiSettings?.ttsVoice || 'shimmer',
    availableVoices: OPENAI_VOICES,
    needsManualFallback: true,
  };
};

/**
 * Speak text using the appropriate TTS service
 * @param {string} userId - User ID
 * @param {string} text - Text to speak
 * @param {Object} options - Additional options
 * @returns {Promise<void>}
 */
export const speak = async (userId, text, options = {}) => {
  const { service, provider, needsManualFallback } = await getTTSService(userId);

  if (needsManualFallback) {
    // Caller should handle OpenAI TTS
    return { provider, needsManualFallback: true };
  }

  if (service) {
    await service.speak(text, options);
    return { provider, needsManualFallback: false };
  }

  throw new Error('No TTS service available');
};

/**
 * Stop current TTS playback
 * @param {string} userId - User ID
 */
export const stopSpeaking = async (userId) => {
  const { service, needsManualFallback } = await getTTSService(userId);
  
  if (!needsManualFallback && service) {
    service.stop();
  }
};

/**
 * Check if currently speaking
 * @param {string} userId - User ID
 * @returns {Promise<boolean>}
 */
export const isSpeaking = async (userId) => {
  const { service, needsManualFallback } = await getTTSService(userId);
  
  if (!needsManualFallback && service) {
    return service.speaking();
  }
  
  return false;
};

/**
 * Get available voices for current provider
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Available voices
 */
export const getAvailableVoices = async (userId) => {
  const preferences = await getVoicePreferences(userId);
  const provider = determineTTSProvider(preferences);

  if (provider === TTS_PROVIDER.GEMINI_FLASH_TTS) {
    return {
      provider,
      voices: GEMINI_LIVE_PREBUILT_VOICES,
      currentVoice: preferences?.geminiLive?.voice || 'Aoede',
    };
  }

  if (provider === TTS_PROVIDER.DEEPGRAM) {
    return {
      provider,
      voices: AURA_VOICES,
      currentVoice: preferences.deepgramSettings?.ttsVoice || 'asteria',
    };
  }

  return {
    provider: TTS_PROVIDER.OPENAI,
    voices: OPENAI_VOICES,
    currentVoice: preferences.openaiSettings?.ttsVoice || 'shimmer',
  };
};

/**
 * Check if Deepgram TTS should be used
 * @param {string} userId - User ID
 * @returns {Promise<boolean>}
 */
export const shouldUseDeepgramTTS = async (userId) => {
  const preferences = await getVoicePreferences(userId);
  return isDeepgramTTSEnabled(preferences);
};

// Export factory object
const ttsFactory = {
  getTTSService,
  determineTTSProvider,
  speak,
  stopSpeaking,
  isSpeaking,
  getAvailableVoices,
  shouldUseDeepgramTTS,
  TTS_PROVIDER,
  AURA_VOICES,
  OPENAI_VOICES,
};

export default ttsFactory;
